import { useContext, useEffect, useState } from 'react'
import { Badge, Button, Loader, toast } from '@mainstem/react-mainstem'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faFileSignature, faHourglassHalf, faUser } from '@fortawesome/pro-light-svg-icons'
import {
  MainStemApi,
  MainStemAPIPlatformControllersPurchasingRFQApprovalsUserListAPIResponseUserDetails
} from 'api-new'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui'
import { AppContext } from 'context'
import { IModalSubmitForApproval } from './types'

const ModalSubmitForApproval: React.FC<IModalSubmitForApproval> = ({ rfqId, onClose, onConfirm, loading }) => {
  const { loggedInUser } = useContext(AppContext)
  const [approvalUsers, setApprovalUsers] = useState<
    MainStemAPIPlatformControllersPurchasingRFQApprovalsUserListAPIResponseUserDetails[]
  >([])
  const [loadingUsers, setLoadingUsers] = useState(true)

  useEffect(() => {
    MainStemApi.api
      .rfqApprovalsUserList()
      .then((apiResponse) => {
        if (apiResponse.data.wasSuccessful) {
          setApprovalUsers(apiResponse.data.users)
        } else {
          toast.error(apiResponse.data.message || 'Failed to load approval users')
        }
      })
      .finally(() => {
        setLoadingUsers(false)
      })
  }, [])

  return (
    <Modal onClose={onClose} size='md' zIndex={1000}>
      <ModalHeader onClose={onClose}>Submit for Approval</ModalHeader>
      <ModalBody>
        <p className='text-sm text-slate-600 mb-4'>
          {rfqId ? (
            <>
              You are about to submit RFQ <span className='font-semibold'>#{rfqId}</span> for approval.
            </>
          ) : (
            <>You are about to create and submit this RFQ for approval.</>
          )}{' '}
          The following users will be notified and can approve or reject this RFQ:
        </p>
        {loadingUsers ? (
          <Loader />
        ) : approvalUsers.length > 0 ? (
          <div className='space-y-2 mb-4'>
            {approvalUsers.map((user) => {
              const isCurrentUser = user.id === loggedInUser.uuid
              return (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                    isCurrentUser ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50 border border-slate-100'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      isCurrentUser
                        ? 'bg-linear-to-br from-indigo-500 to-purple-600'
                        : 'bg-linear-to-br from-slate-300 to-slate-400'
                    }`}
                  >
                    <FontAwesomeIcon icon={faUser} className='text-xs text-white' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <span
                      className={`text-sm font-medium truncate block ${
                        isCurrentUser ? 'text-indigo-900' : 'text-slate-700'
                      }`}
                    >
                      {user.username}
                    </span>
                    {isCurrentUser && <span className='text-xs text-indigo-500 font-medium'>You</span>}
                  </div>
                  {isCurrentUser && (
                    <FontAwesomeIcon icon={faCheckCircle} className='text-indigo-500 text-sm shrink-0' />
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <p className='text-sm text-slate-500 mb-4'>No approval users found.</p>
        )}
        <p className='text-sm text-slate-500'>
          The RFQ status will be changed to{' '}
          <Badge color='warning' icon={faHourglassHalf}>
            Pending Approval
          </Badge>
          .
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color='light' onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button color='fancy' icon={faFileSignature} onClick={onConfirm} loading={loading} disabled={loading}>
          Submit for Approval
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export { ModalSubmitForApproval }
